"use client";

import { useState } from "react";
import Link from "next/link";
import CdnImage from "@/components/CdnImage";
import type { EventData } from "@/hooks/useEventQuery";
import { useSmartActivityMap } from "@/hooks/useSmartActivityMap";
import { useMatchingMarkets } from "@/hooks/useMatchingMarkets";
import { extractKalshiEventTicker } from "@/lib/source-url";
import WhaleIcon from "@/app/components/icons/WhaleIcon";
import Tooltip from "@/app/components/Tooltip";
import KalshiLiveScoreBadge from "./KalshiLiveScoreBadge";
import { BookmarkIcon } from "@/app/components/icons/BookmarkIcon";
import { useMarketWatchlistOptional } from "@/contexts/MarketWatchlistContext";
import { useAuth } from "@/hooks/useAuth";
import { useT } from "@/hooks/useT";

// Predexon outage: ocultar solo los badges dependientes de Predexon
// (whale/smart cohort + "Also on Kalshi" matching). El resto del hero intacto.
const HIDE_PREDEXON = process.env.NEXT_PUBLIC_HIDE_PREDEXON_TABS === "true";

interface EventHeroProps {
  eventData: EventData;
  isActive: boolean;
  source: 'polymarket' | 'kalshi' | 'predict' | 'hyperliquid';
  activeMarketsCount: number;
  formatVolume: (vol: number | string | null | undefined) => string;
  slug?: string;
  isCryptoLive?: boolean;
}

const SOURCE_CONFIG: Record<string, { color: string; icon: string; label: string; logoBg?: string }> = {
  predict:     { color: '#6366F1', icon: '',                                  label: 'Predict.fun' },
  kalshi:      { color: '#00D26A', icon: '/logos/kalshi/k-icon.svg',          label: 'Kalshi' },
  polymarket:  { color: '#0066FF', icon: '/logos/polymarket/icon-white.png',  label: 'Polymarket' },
  hyperliquid: { color: '#97FCE4', icon: '/logos/hyperliquid/symbol-mint.svg', label: 'Hyperliquid', logoBg: '#0f1a1f' },
};

/** Build external URL for the source platform */
function getExternalUrl(source: string, slug: string, eventData: EventData): string {
  if (source === 'predict') return `https://predict.fun/market/${slug}`;
  if (source === 'polymarket') return `https://polymarket.com/event/${slug}`;
  // Hyperliquid does NOT expose a deep-linkable URL for HIP-4 outcome
  // markets on app.hyperliquid.xyz. HIP-4 outcomes live in `outcomeMeta`
  // (separate from the spot universe), and the spot path /trade/@N points
  // at a regular spot pair (e.g. @30 = FARMED), not the BTC binary the
  // user clicked. Linking to that would dump the user on the wrong
  // market. Until HL ships an official outcomes UI, send them to the
  // app homepage rather than to a confidently-wrong deep link.
  if (source === 'hyperliquid') {
    return 'https://app.hyperliquid.xyz/';
  }
  // Kalshi: /markets/{seriesTicker}/{title-slug}/{eventTicker}
  const ticker = slug.toLowerCase();
  const seriesTicker = (eventData.seriesTicker || ticker.replace(/-\d[a-z0-9]*$/i, '') || ticker).toLowerCase();
  const titleSlug = (eventData.title || seriesTicker)
    .toLowerCase()
    .replace(/['']/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 80);
  return `https://kalshi.com/markets/${seriesTicker}/${titleSlug}/${ticker}`;
}

/** Mobile: circular badge (same as SourceSelector) */
function SourceBadge({ source }: { source: string }) {
  const cfg = SOURCE_CONFIG[source];
  if (!cfg) return null;
  return (
    <div className="w-5 h-5 rounded-[5px] flex items-center justify-center flex-shrink-0" style={{ backgroundColor: cfg.logoBg ?? cfg.color }}>
      {cfg.icon ? (
        <img src={cfg.icon} alt={cfg.label} className="w-3.5 h-3.5 object-contain" />
      ) : (
        <span className="text-[10px] font-black text-black leading-none">P</span>
      )}
    </div>
  );
}

/** Desktop: full logo (same style as TradingModal) */
function SourceLogoFull({ source }: { source: string }) {
  if (source === 'predict') return <img src="/logos/predictfun/Predict-Logo-Purp.svg" alt="Predict.fun" className="h-4 opacity-60 hover:opacity-100 transition-opacity" />;
  if (source === 'kalshi') return <img src="/logos/kalshi/kalshi-logo.svg" alt="Kalshi" className="h-3.5 opacity-50 hover:opacity-100 transition-opacity brightness-0 dark:brightness-100" />;
  if (source === 'hyperliquid') {
    // Brand asset is mint-on-dark; we use the green/white wordmark in dark
    // mode and drop opacity slightly to match the polymarket/kalshi treatment.
    return (
      <img src="/logos/hyperliquid/logo-green-white.svg" alt="Hyperliquid" className="h-3.5 opacity-70 hover:opacity-100 transition-opacity" />
    );
  }
  return (
    <>
      <img src="/logos/polymarket/logo-black.svg" alt="Polymarket" className="h-3.5 opacity-50 hover:opacity-100 transition-opacity dark:hidden" />
      <img src="/logos/polymarket/logo-white.svg" alt="Polymarket" className="h-3.5 opacity-50 hover:opacity-100 transition-opacity hidden dark:block" />
    </>
  );
}

export default function EventHero({ eventData, isActive, source, activeMarketsCount, formatVolume, slug, isCryptoLive }: EventHeroProps) {
  const t = useT();
  const { isLoggedIn, login } = useAuth();
  const watchlistContext = useMarketWatchlistOptional();
  // Watchlist the event by its slug — same key EventCard uses elsewhere.
  // Picks the highest-volume nested market's conditionId as the row context
  // so watchlist entries keep a stable reference even when markets churn.
  const eventWatchlistKey = slug || eventData.slug;
  const watchlistConditionId = (() => {
    const markets: any[] = (eventData as any).markets || [];
    if (markets.length === 0) return undefined;
    let best: any = markets[0];
    let bestVol = Number(best?.volume || 0);
    for (const m of markets) {
      const v = Number(m?.volume || 0);
      if (v > bestVol) { best = m; bestVol = v; }
    }
    return best?.conditionId;
  })();
  const isBookmarked = eventWatchlistKey ? (watchlistContext?.isInWatchlist(eventWatchlistKey) ?? false) : false;
  // Cohort badges — uses shared bulk smart-activity map (already fetched by
  // list views; on the event page it fires its own cheap fetch). Picks the
  // nested market with the highest smart wallet count as the leader signal.
  const { data: cohortMap } = useSmartActivityMap(500, source === 'polymarket');
  const cohortRow = (() => {
    if (!cohortMap || source !== 'polymarket') return null;
    const markets: any[] = (eventData as any).markets || [];
    let best: { wallets: number; volume: number } | null = null;
    for (const m of markets) {
      const cid = (m?.conditionId || '').toLowerCase();
      if (!cid) continue;
      const row = cohortMap.get(cid);
      if (!row) continue;
      if (!best || row.smartWalletCount > best.wallets) {
        best = { wallets: row.smartWalletCount, volume: row.smartVolume };
      }
    }
    return best;
  })();
  const isWhaleMarket = !HIDE_PREDEXON && !!cohortRow && cohortRow.volume > 500_000 && cohortRow.wallets >= 10;
  const isSmartHeavy = !HIDE_PREDEXON && !!cohortRow && cohortRow.wallets >= 30 && !isWhaleMarket;

  // Cross-venue match lookup (Polymarket ↔ Kalshi) — picks the leader
  // market's conditionId for the query. Fires once per event.
  const leaderConditionId = (() => {
    if (source !== 'polymarket') return null;
    const markets: any[] = (eventData as any).markets || [];
    if (markets.length === 0) return null;
    let best: any = markets[0];
    let bestVol = Number(best?.volume || 0);
    for (const m of markets) {
      const v = Number(m?.volume || 0);
      if (v > bestVol) { best = m; bestVol = v; }
    }
    return best?.conditionId || null;
  })();
  const { data: matchData } = useMatchingMarkets(leaderConditionId, source === 'polymarket');
  const kalshiMatches = matchData?.matches?.KALSHI;
  const hasExactMatch = !!(kalshiMatches?.exact && kalshiMatches.exact.length > 0);
  const hasRelatedMatch = !!(kalshiMatches?.related && kalshiMatches.related.length > 0);
  const hasAnyMatch = !HIDE_PREDEXON && (hasExactMatch || hasRelatedMatch);

  // Mobile: collapse context badges into "+N" button
  const [contextExpanded, setContextExpanded] = useState(false);

  return (
    <div className="relative px-3 md:px-4 pt-3 md:pt-4 pb-1">
      {/* Title row: image + title + badges + stats */}
      <div className="flex items-center md:items-start gap-2.5 md:gap-4 pr-20 md:pr-24">
        {/* Event image thumbnail */}
        {eventData.image && (
          <div className="relative flex-shrink-0 rounded-[5px] md:rounded-[5px] overflow-hidden bg-gray-100 dark:bg-[#0f0f12] border border-gray-200 dark:border-[#1A1A1A] w-10 h-10 md:w-20 md:h-20">
            <CdnImage src={eventData.image} alt="" fill className="object-cover" loading="lazy" onError={(e) => { (e.target as HTMLImageElement).parentElement!.style.display = 'none'; }} />
          </div>
        )}

        <div className="flex-1 min-w-0">
          {/* Source logo + bookmark — absolute top-right. Absolute
              positioning (vs the previous float-right) keeps the title/badge
              column free to wrap naturally without the icons eating into the
              text flow on narrow screens. */}
          {slug && SOURCE_CONFIG[source] && (
            <div className="absolute top-3 right-3 md:top-4 md:right-4 inline-flex items-center gap-2 z-10">
              {eventWatchlistKey && (
                <Tooltip content={isBookmarked ? t("hero.bookmark.remove") : t("hero.bookmark.add")}>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (!isLoggedIn) { login(); return; }
                      watchlistContext?.toggleWatchlist(eventWatchlistKey, eventData.title, watchlistConditionId, source);
                    }}
                    className="flex items-center justify-center w-7 h-7 rounded-[5px] hover:bg-black/5 dark:hover:bg-white/5 transition-colors text-gray-500 hover:text-gray-900 dark:hover:text-white"
                    aria-label={isBookmarked ? t("hero.bookmark.remove") : t("hero.bookmark.add")}
                  >
                    <BookmarkIcon
                      size={16}
                      isBookmarked={isBookmarked}
                      fillColor={SOURCE_CONFIG[source].color}
                    />
                  </button>
                </Tooltip>
              )}
              <a href={getExternalUrl(source, slug, eventData)} target="_blank" rel="noopener noreferrer nofollow" className="opacity-70 hover:opacity-100 transition-opacity" title={t("hero.viewOn", { source: SOURCE_CONFIG[source].label })}>
                <span className="md:hidden"><SourceBadge source={source} /></span>
                <span className="hidden md:inline-flex"><SourceLogoFull source={source} /></span>
              </a>
            </div>
          )}
          {/* Build all badge items */}
          {(() => {
            // Alert badges (colorful)
            const alertBadges: { key: string; node: React.ReactNode }[] = [];
            if (eventData.closed) {
              alertBadges.push({ key: 'closed', node: <span className="inline-flex items-center h-[20px] px-2.5 rounded-[5px] text-[10px] leading-none font-bold font-inter-tight bg-red-500 text-white">{t("hero.badge.closed")}</span> });
            }
            if (isCryptoLive) {
              alertBadges.push({ key: 'live', node: <span className="inline-flex items-center h-[20px] px-2.5 rounded-[5px] text-[10px] leading-none font-bold font-inter-tight bg-red-500 text-white animate-pulse">{t("hero.badge.live")}</span> });
            }
            if (eventData.competitive != null && eventData.competitive > 0.8) {
              alertBadges.push({
                key: 'hot',
                node: (
                  <Tooltip content={t("hero.tooltip.hot", { pct: ((eventData.competitive ?? 0) * 100).toFixed(1) })}>
                    <span className="inline-flex items-center h-[20px] px-2.5 rounded-[3px] text-[10px] leading-none font-bold font-inter-tight bg-gray-100 dark:bg-[#1a1a1a] text-gray-500 dark:text-gray-400">{t("hero.badge.hot")}</span>
                  </Tooltip>
                ),
              });
            }
            if (!eventData.closed) {
              const created = (eventData as any).startDate || (eventData as any).createdAt;
              if (created) {
                const ageHrs = (Date.now() - new Date(created).getTime()) / 36e5;
                if (ageHrs >= 0 && ageHrs < 24) {
                  alertBadges.push({
                    key: 'new',
                    node: (
                      <Tooltip content={t("hero.tooltip.new", { n: Math.round(ageHrs) })}>
                        <span className="inline-flex items-center h-[20px] px-2.5 rounded-[3px] text-[10px] leading-none font-bold font-inter-tight bg-gray-100 dark:bg-[#1a1a1a] text-gray-500 dark:text-gray-400">{t("hero.badge.new")}</span>
                      </Tooltip>
                    ),
                  });
                }
              }
            }
            {/* Time badge removed — too noisy */}
            {
              const markets: any[] = (eventData as any).markets || [];
              const isDisputed = markets.some((m) => {
                let s: any = m?.umaResolutionStatuses;
                if (typeof s === 'string') { try { s = JSON.parse(s); } catch { s = [s]; } }
                if (Array.isArray(s) && s.includes('disputed')) return true;
                return m?.umaResolutionStatus === 'disputed';
              });
              // Only show disputed badge for active events (not relevant once resolved)
              if (isActive && isDisputed) {
                alertBadges.push({
                  key: 'disputed',
                  node: (
                    <a href="/uma" className="inline-flex items-center h-[20px] px-2.5 rounded-[5px] text-[10px] leading-none font-bold font-inter-tight bg-red-500 text-white hover:bg-red-600 transition-colors" title={t("hero.tooltip.disputed")}>{t("hero.badge.disputed")}</a>
                  ),
                });
              }
            }
            if (source === 'kalshi') {
              alertBadges.push({
                key: 'kalshi-live',
                node: <KalshiLiveScoreBadge eventTicker={(eventData as any).eventTicker || (eventData as any).id || (slug || '').toUpperCase()} />,
              });
            }

            // Context badges (subtle)
            const contextBadges: { key: string; node: React.ReactNode }[] = [];
            // Category badge — show in alertBadges for closed events (visible on mobile), else contextBadges
            if (eventData.tags && eventData.tags.length > 0) {
              const mainCategories = ['politics', 'sports', 'crypto', 'economy', 'economics', 'finance', 'tech', 'technology', 'science', 'entertainment', 'world', 'us', 'elections', 'culture', 'climate', 'health', 'business', 'gaming', 'music', 'ai', 'geopolitics', 'weather', 'financials', 'energy'];
              const tag = eventData.tags.map((t: any) => typeof t === 'string' ? t : (t.label || t.slug || t.name || '')).find((l: string) => l && mainCategories.includes(l.toLowerCase()));
              if (tag) {
                const catBadge = { key: 'cat', node: <span className="inline-flex items-center h-[20px] px-2.5 rounded-[3px] text-[10px] leading-none font-bold font-inter-tight bg-gray-100 dark:bg-[#1a1a1a] text-gray-500 dark:text-gray-400">{tag.toUpperCase()}</span> };
                // For closed events, add to alertBadges so it shows on mobile without +1
                if (eventData.closed) {
                  alertBadges.push(catBadge);
                } else {
                  contextBadges.push(catBadge);
                }
              }
            }
            // Hide whale/smart/kalshi badges for closed events (not actionable)
            if (isActive && isWhaleMarket) {
              contextBadges.push({
                key: 'whale',
                node: (
                  <Tooltip content={t("hero.tooltip.whale", { wallets: cohortRow!.wallets, vol: (cohortRow!.volume/1_000_000).toFixed(1) })}>
                    <span className="inline-flex items-center gap-1 h-[20px] px-2.5 rounded-[3px] text-[10px] leading-none font-bold font-inter-tight bg-gray-100 dark:bg-[#1a1a1a] text-gray-500 dark:text-gray-400">
                      <WhaleIcon size={11} />
                      <span className="hidden md:inline">{t("hero.badge.whale")}</span>
                    </span>
                  </Tooltip>
                ),
              });
            }
            if (isActive && isSmartHeavy) {
              contextBadges.push({
                key: 'smart',
                node: (
                  <Tooltip content={t("hero.tooltip.smart", { wallets: cohortRow!.wallets })}>
                    <span className="inline-flex items-center h-[20px] px-2.5 rounded-[3px] text-[10px] leading-none font-bold font-inter-tight bg-gray-100 dark:bg-[#1a1a1a] text-gray-500 dark:text-gray-400">{t("hero.badge.smart")}</span>
                  </Tooltip>
                ),
              });
            }
            if (isActive && hasAnyMatch) {
              // Already deduped by title at the useMatchingMarkets hook
              // layer (Predexon emits one row per Kalshi sub-market in a
              // series so identical-title rows arrive N times). Tooltip
              // can render straight from the result.
              const list = hasExactMatch ? kalshiMatches!.exact! : kalshiMatches!.related!;
              const tooltipLines = [
                hasExactMatch ? t("hero.tooltip.kalshi.exact") : t("hero.tooltip.kalshi.related"),
                ...list.slice(0, 3).map((m) => `• ${m.title?.slice(0, 60) || m.market_ticker}`),
                list.length > 3 ? t("hero.tooltip.kalshi.more", { n: list.length - 3 }) : null,
              ].filter(Boolean).join('\n');
              const firstTicker = list[0]?.market_ticker;
              const eventTicker = firstTicker ? extractKalshiEventTicker(firstTicker) : null;
              const href = eventTicker ? `/kalshi/event/${eventTicker}` : '/kalshi';
              contextBadges.push({
                key: 'kalshi',
                node: (
                  <Tooltip content={tooltipLines} position="bottom">
                    <Link href={href} prefetch={false} className="inline-flex items-center gap-1 h-[20px] px-2.5 rounded-[3px] text-[10px] leading-none font-bold font-inter-tight bg-gray-100 dark:bg-[#1a1a1a] text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-[#252525] transition-colors no-underline align-middle">
                      {/* `block` on the img prevents baseline-induced shift
                          inside an inline-flex anchor, which was making this
                          badge sit ~1-2px off vs the sibling SMART/HOT pills. */}
                      <img src="/logos/kalshi/k-icon.svg" alt="" className="block w-2.5 h-2.5 flex-shrink-0" />
                      KALSHI
                    </Link>
                  </Tooltip>
                ),
              });
            }

            const allBadges = [...alertBadges, ...contextBadges];
            const MAX_VISIBLE_DESKTOP = 5;
            const MAX_VISIBLE_MOBILE = 3;
            const visibleDesktop = allBadges.slice(0, MAX_VISIBLE_DESKTOP);
            const hiddenDesktop = allBadges.slice(MAX_VISIBLE_DESKTOP);
            const contextCount = contextBadges.length;
            // Mobile only collapses context badges behind a "+X" pill when
            // the alert + context total would exceed the 3-badge budget.
            // With ≤ 3 badges total there's no benefit to hiding anything —
            // the row fits comfortably and the user shouldn't have to tap
            // a "+1" just to see a single category badge.
            // Badges are now subtle gray (no longer competing colors), so on
            // mobile we show them all inline instead of collapsing behind a
            // "+X" pill — the row reads fine and the user sees every signal.
            void contextCount; void MAX_VISIBLE_MOBILE;
            const showMobileCollapsePill = false;

            return (
              <>
                {/* Desktop: all badges in 1 row, +X if > MAX_VISIBLE.
                    Wrapper is `inline-flex items-center` so the badge inside
                    (whether it's a <span> or an <a> with an <img>) sits at
                    the same vertical center — a plain inline <span> wrapper
                    inherits a different baseline when its child contains
                    images, which was pushing the KALSHI badge ~1-2px up. */}
                <div className="hidden md:flex items-center gap-1.5 mb-1 flex-wrap min-h-[22px]">
                  {(contextExpanded ? allBadges : visibleDesktop).map((b) => (
                    <span key={b.key} className="inline-flex items-center">{b.node}</span>
                  ))}
                  {!contextExpanded && hiddenDesktop.length > 0 && (
                    <button
                      onClick={() => setContextExpanded(true)}
                      className="inline-flex items-center h-[20px] px-2.5 rounded-[5px] text-[10px] font-bold font-inter-tight bg-gray-100 dark:bg-[#1a1a1a] text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-[#252525] transition-colors"
                    >
                      +{hiddenDesktop.length}
                    </button>
                  )}
                  {contextExpanded && hiddenDesktop.length > 0 && (
                    <button
                      onClick={() => setContextExpanded(false)}
                      className="inline-flex items-center justify-center w-[20px] h-[20px] rounded-[5px] text-[10px] font-bold bg-gray-100 dark:bg-[#1a1a1a] text-gray-400 hover:bg-gray-200 dark:hover:bg-[#252525] transition-colors"
                    >
                      ✕
                    </button>
                  )}
                </div>

                {/* Mobile: alert + context badges in ONE flex-wrap row.
                    Context badges are hidden behind a "+X" pill only when
                    the total exceeds MAX_VISIBLE_MOBILE — with ≤ 3 badges
                    everything renders inline. When the user expands, the
                    extra badges flow into the same row (flex-wrap pushes
                    them down only when they actually overflow), instead
                    of the previous design that always dropped them to a
                    new line. The ✕ collapse button only appears when the
                    row was originally collapsible. */}
                <div className="flex md:hidden items-center gap-1.5 mb-1 flex-nowrap overflow-x-auto scrollbar-hide min-h-[22px]">
                  {alertBadges.map((b) => <span key={b.key} className="inline-flex items-center flex-shrink-0">{b.node}</span>)}
                  {showMobileCollapsePill && !contextExpanded && (
                    <button
                      onClick={() => setContextExpanded(true)}
                      className="inline-flex items-center h-[20px] px-2.5 rounded-[5px] text-[10px] font-bold font-inter-tight bg-gray-100 dark:bg-[#1a1a1a] text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-[#252525] transition-colors"
                    >
                      +{contextCount}
                    </button>
                  )}
                  {/* Mobile keeps only category + Kalshi context badges (HOT
                      lives in alertBadges above). Whale / Smart are desktop-only
                      to keep the mobile row tight. */}
                  {contextBadges.filter((b) => b.key === 'cat' || b.key === 'kalshi').map((b) => (
                    <span key={b.key} className="inline-flex items-center flex-shrink-0">{b.node}</span>
                  ))}
                  {showMobileCollapsePill && contextExpanded && (
                    <button
                      onClick={() => setContextExpanded(false)}
                      className="inline-flex items-center justify-center w-[20px] h-[20px] rounded-[5px] text-[10px] font-bold bg-gray-100 dark:bg-[#1a1a1a] text-gray-400 hover:bg-gray-200 dark:hover:bg-[#252525] transition-colors"
                    >
                      ✕
                    </button>
                  )}
                </div>
              </>
            );
          })()}

          {/* Title — render gamma's raw title verbatim. We previously
              rewrote "on May 28" → "today" for the current day's market,
              but the series-pills navigator (above the title) already
              shows "May 28" as the active chip, so collapsing the title
              to "today" duplicated information and broke continuity when
              jumping between days. Polymarket itself keeps the date in
              the title for the same reason. */}
          <h1 className="text-sm md:text-xl font-monument font-bold text-gray-900 dark:text-white leading-tight break-words">
            {eventData.title}
          </h1>

          {/* Stat badges — desktop inline. Total volume is shown in the chart
              footer (Polymarket parity) so the hero only carries the
              complementary 24h volume + change and the liquidity/open-interest
              line. Hidden on short-lived crypto up/down events where volumes
              read as noise. */}
          {!isCryptoLive && (
          <div className="hidden md:flex flex-wrap items-center gap-x-2 mt-1.5 text-[11px] font-inter-tight font-medium text-gray-500 dark:text-gray-400">
            <span className="tabular-nums">
              {formatVolume((source === 'kalshi' && !eventData.volume24hr) ? eventData.volume : eventData.volume24hr)} {t("common.24h")}
              {eventData.oneDayPriceChange != null && !isNaN(eventData.oneDayPriceChange) && eventData.oneDayPriceChange !== 0 && (
                <span className={`ml-1 ${eventData.oneDayPriceChange > 0 ? 'text-[#00FF9D]' : 'text-[#FF1744]'}`}>
                  {eventData.oneDayPriceChange > 0 ? '↗' : '↘'}{Math.abs(eventData.oneDayPriceChange * 100).toFixed(1)}%
                </span>
              )}
            </span>
            <span className="text-gray-300 dark:text-gray-600">·</span>
            <span className="tabular-nums">
              {formatVolume(((source === 'kalshi' || source === 'hyperliquid') && eventData.openInterest) ? eventData.openInterest : eventData.liquidity)} {((source === 'kalshi' || source === 'hyperliquid') && eventData.openInterest) ? t("common.openInterest") : t("trading.liquidity")}
            </span>
          </div>
          )}
        </div>
      </div>

      {/* Stat badges — mobile below image+title. Same crypto-live gate as desktop. */}
      {!isCryptoLive && (
      <div className="flex md:hidden flex-wrap items-center gap-x-1.5 mt-2 text-[10px] font-inter-tight font-medium text-gray-500 dark:text-gray-400">
        <span className="tabular-nums">
          {formatVolume((source === 'kalshi' && !eventData.volume24hr) ? eventData.volume : eventData.volume24hr)} {t("common.24h")}
        </span>
        <span className="text-gray-300 dark:text-gray-600">·</span>
        <span className="tabular-nums">
          {formatVolume(((source === 'kalshi' || source === 'hyperliquid') && eventData.openInterest) ? eventData.openInterest : eventData.liquidity)} {((source === 'kalshi' || source === 'hyperliquid') && eventData.openInterest) ? t("common.oi") : t("trading.liquidity")}
        </span>
      </div>
      )}
    </div>
  );
}
